/**
 * @genesis_domain Auth Engine
 * @genesis_version V9.1
 * @genesis_intent Profile creation page — collects user culinary preferences after initial signup.
 * @genesis_boundaries No business logic beyond form state. Pure presentational with semantic tokens.
 * @genesis_ai_trail V18.0: Z-Axis Standardization Strike — eradicated all literal Tailwind colors.
 */

import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext.js';

export const ProfileCreation = () => {
  const { currentUser, isInitializing } = useAuth();
  const [profileName, setProfileName] = useState('');
  const [cookingStyle, setCookingStyle] = useState('');
  const [favoriteCuisine, setFavoriteCuisine] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('beginner');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!profileName.trim()) {
      setError('Profile name is required');
      return;
    }

    try {
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile');
    }
  };

  return (
    <div className="h-full w-full theme-canvas flex items-center justify-center p-4">
      <div className="theme-card rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden border border-[var(--color-glass-border)]">
        <div className="p-6 sm:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--color-card-text)] mb-2">
              Complete Your User Profile
            </h1>
            <p className="text-[var(--color-card-text)] opacity-70 text-sm sm:text-base">
              Tell us about your culinary preferences
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[var(--color-danger-subtle)] border border-[var(--color-danger-base)]/20 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-[var(--color-danger-base)] mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm-7-4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-[var(--color-card-text)] text-sm sm:text-base">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-[var(--color-success-subtle)] border border-[var(--color-success-base)]/20 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-[var(--color-success-base)] mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-[var(--color-card-text)] text-sm sm:text-base">
                  Profile created successfully! Redirecting to your vault...
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="sm:col-span-2">
                <label htmlFor="profileName" className="block text-base sm:text-lg font-medium text-[var(--color-card-text)] opacity-80 mb-2">
                  Profile Name *
                </label>
                <input
                  id="profileName"
                  type="text"
                  value={profileName}
                  onChange={e => setProfileName(e.target.value)}
                  className="w-full px-4 py-3 theme-input border border-[var(--color-input-border)] rounded-lg text-base sm:text-lg text-[var(--color-input-text)] placeholder:text-[var(--color-content-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-primary-base)] transition"
                  placeholder="e.g., My Cooking Profile"
                  required
                  disabled={isInitializing || success}
                />
              </div>

              <div>
                <label htmlFor="cookingStyle" className="block text-base sm:text-lg font-medium text-[var(--color-card-text)] opacity-80 mb-2">
                  Cooking Style
                </label>
                <input
                  id="cookingStyle"
                  type="text"
                  value={cookingStyle}
                  onChange={e => setCookingStyle(e.target.value)}
                  className="w-full px-4 py-3 theme-input border border-[var(--color-input-border)] rounded-lg text-base sm:text-lg text-[var(--color-input-text)] placeholder:text-[var(--color-content-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-primary-base)] transition"
                  placeholder="e.g., Traditional, Fusion, Healthy"
                  disabled={isInitializing || success}
                />
              </div>

              <div>
                <label htmlFor="favoriteCuisine" className="block text-base sm:text-lg font-medium text-[var(--color-card-text)] opacity-80 mb-2">
                  Favorite Cuisine
                </label>
                <input
                  id="favoriteCuisine"
                  type="text"
                  value={favoriteCuisine}
                  onChange={e => setFavoriteCuisine(e.target.value)}
                  className="w-full px-4 py-3 theme-input border border-[var(--color-input-border)] rounded-lg text-base sm:text-lg text-[var(--color-input-text)] placeholder:text-[var(--color-content-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-primary-base)] transition"
                  placeholder="e.g., Italian, Mexican, Chinese"
                  disabled={isInitializing || success}
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="experienceLevel" className="block text-base sm:text-lg font-medium text-[var(--color-card-text)] opacity-80 mb-2">
                  Cooking Experience Level
                </label>
                <select
                  id="experienceLevel"
                  value={experienceLevel}
                  onChange={e => setExperienceLevel(e.target.value)}
                  className="w-full px-4 py-3 theme-input border border-[var(--color-input-border)] rounded-lg text-base sm:text-lg text-[var(--color-input-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-primary-base)] transition"
                  disabled={isInitializing || success}
                >
                  <option value="beginner">Beginner - Just starting out</option>
                  <option value="intermediate">Intermediate - Comfortable in the kitchen</option>
                  <option value="advanced">Advanced - Experienced home cook</option>
                  <option value="expert">Expert - Professional or passionate chef</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 pt-4">
              <button
                type="submit"
                disabled={isInitializing || success}
                className="flex-1 py-3 px-4 bg-[var(--color-primary-base)] hover:bg-[var(--color-primary-hover)] text-[var(--color-content-inverse)] text-base sm:text-lg font-medium rounded-full transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isInitializing ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[var(--color-content-inverse)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating Profile...
                  </span>
                ) : (
                  <span>Complete User Profile</span>
                )}
              </button>

              <Link
                to="/"
                className="flex-1 py-3 px-4 bg-[var(--color-surface-elevated)] border border-[var(--color-glass-border)] text-[var(--color-card-text)] text-base sm:text-lg font-medium rounded-full text-center hover:bg-[var(--color-primary-base)] hover:text-[var(--color-content-inverse)] hover:border-[var(--color-primary-base)] transition-all duration-300"
              >
                Skip for Now
              </Link>
            </div>
          </form>

          <div className="mt-6 sm:mt-8 pt-6 border-t border-[var(--color-glass-border)]">
            <p className="text-center text-[var(--color-card-text)] opacity-60 text-sm sm:text-base">
              This helps us personalize your recipe recommendations
            </p>
          </div>
        </div>

        <div className="px-6 sm:px-8 py-4 theme-footer border-t border-[var(--color-glass-border)]">
          <div className="flex items-center justify-center">
            <span className="text-xs text-[var(--color-footer-text)]">HOME RECIPE VAULT • MODERN EDITION</span>
          </div>
        </div>
      </div>
    </div>
  );
};